import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Toolbar,
  Button,
  IconButton,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  Chip,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Tooltip,
  Switch,
  Checkbox,
  Pagination,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  HelpOutline as PendingIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PostAdd as PostAddIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  ChevronRight as ChevronRightIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

import { messageShow, uploadSingle } from "../../common/api/actions";
import { getDocuments, getDocumentGroups } from "../api/actions/query";
import { createDocument, removeDocument, updateDocument, documentPublishToggle } from "../api/actions/mutation";
import BrandedLoading from "../../common/BrandedLoading";
import { checkPermission } from "../../common/Sidebar/checkPermission";
import styles from "./styles";

const Documents = ({ classes }) => {
  const dispatch = useDispatch();
  const { list: documents, groups: rawGroups, count, isLoading } = useSelector(state => state.documents);

  const [isUploading, setIsUploading] = useState({});
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [newDocTitle, setNewDocTitle] = useState("");
  const [newDocGroup, setNewDocGroup] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [storageType, setStorageType] = useState("");
  const [selectedGroupFilter, setSelectedGroupFilter] = useState("all");
  const [selectedFile, setSelectedFile] = useState(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 6;
  const [isPermissionChecking, setIsPermissionChecking] = useState(true);
  const [permissions, setPermissions] = useState({
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false
  });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isHardDelete, setIsHardDelete] = useState(false);

  const groups = useMemo(() => (rawGroups || []).map(groupName => ({
    value: groupName,
    label: groupName,
  })), [rawGroups]);

  const fetchDocuments = useCallback(async (groupFilter = "all", pageNum = 1) => {
    try {
      await dispatch(getDocuments({
        group: groupFilter,
        page: pageNum,
        limit: rowsPerPage
      }));
    } catch (error) {
      dispatch(messageShow("Failed to load documents."));
    }
  }, [dispatch, rowsPerPage]);

  const fetchGroups = useCallback(async () => {
    try {
      await dispatch(getDocumentGroups());
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  }, [dispatch]);

  const verifyPermissions = useCallback(async () => {
    setIsPermissionChecking(true);
    try {
      const modulePermission = await checkPermission("utilities", "documentManager");
      if (modulePermission) {
        setPermissions({
          canEdit: modulePermission.canEdit,
          canDelete: modulePermission.canDelete,
          canCreate: modulePermission.canCreate,
          canView: modulePermission.canView
        });
      }
    } catch (error) {
      console.error("Error verifying permissions:", error);
    } finally {
      setIsPermissionChecking(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments("all");
    fetchGroups();
    verifyPermissions();
  }, [fetchDocuments, fetchGroups, verifyPermissions]);

  const handlePageChange = (event, value) => {
    setPage(value);
    fetchDocuments(selectedGroupFilter, value);
  };


  const onUpload = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (file.type !== "application/pdf") {
      dispatch(messageShow("Please upload a PDF file."));
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      dispatch(messageShow("File size should not exceed 15MB."));
      return;
    }

    setSelectedFile(file);
  };

  const handleEditStart = (doc) => {
    setEditingDoc(doc);
    setNewDocTitle(doc.title);
    setNewDocGroup(doc.group || "");
    setIsPublished(doc.isPublished !== undefined ? doc.isPublished : true);
    setStorageType(doc.storageType || "");
    setIsAddDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedFile && !editingDoc) {
      dispatch(messageShow("Please select a file first."));
      return;
    }

    const finalGroup = newDocGroup ? newDocGroup.trim() : "";
    const tempId = `uploading_${Date.now()}`;

    setIsUploading(prev => ({ ...prev, [tempId]: true }));

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("type", "document");
    formData.append("title", newDocTitle);
    formData.append("group", finalGroup);
    formData.append("storageType", storageType);

    try {
      let fileUrl = editingDoc ? editingDoc.fileUrl : "";

      if (selectedFile) {
        const uploadResponse = await uploadSingle(formData);
        const uploadData = uploadResponse.data || uploadResponse;

        if (uploadData.success) {
          fileUrl = uploadData.file || uploadData.url;
        } else {
          dispatch(messageShow(uploadData.message || "Upload failed."));
          return;
        }
      }

      if (editingDoc) {
        const updates = {
          title: newDocTitle,
          group: finalGroup,
          storageType: storageType,
          fileUrl: fileUrl,
          isPublished: isPublished,
        };

        const { data: updateData } = await dispatch(updateDocument(editingDoc._id, updates));

        if (updateData.success) {
          dispatch(messageShow("Document updated successfully."));
          setIsAddDialogOpen(false);
          setSelectedFile(null);
          setEditingDoc(null);
          fetchDocuments(selectedGroupFilter);
        } else {
          dispatch(messageShow(updateData.message || "Failed to update document."));
        }
      } else {
        // Step 2: Metadata Storage
        const { data: metaData } = await dispatch(createDocument({
          title: newDocTitle,
          fileUrl: fileUrl,
          group: finalGroup,
          storageType: storageType,
          isPublished: isPublished,
        }));

        if (metaData.success) {
          dispatch(messageShow("Document uploaded and saved successfully."));
          setIsAddDialogOpen(false);
          setSelectedFile(null);
          fetchDocuments(selectedGroupFilter);
        } else {
          dispatch(messageShow(metaData.message || "Failed to save document."));
        }
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      const errorMessage = error.response?.data?.message || "Upload failed. Please try again.";
      dispatch(messageShow(errorMessage));
    } finally {
      setIsUploading(prev => ({ ...prev, [tempId]: false }));
      if (!editingDoc) {
        setNewDocTitle("");
        setNewDocGroup("");
        setIsPublished(true);
      }
      setStorageType("");
      setSelectedFile(null);
    }
  };

  const onPublishToggle = useCallback(async (doc) => {
    const newPublishedStatus = !doc.isPublished;
    const confirmMessage = `Are you sure you want to ${newPublishedStatus ? 'publish' : 'unpublish'} this document?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const { data } = await dispatch(documentPublishToggle(doc._id, newPublishedStatus));
      if (data.success) {
        dispatch(messageShow(data.message || `Document ${newPublishedStatus ? 'published' : 'unpublished'} successfully.`));
        fetchDocuments(selectedGroupFilter);
      } else {
        dispatch(messageShow(data.message || "Failed to update document status."));
      }
    } catch (error) {
      console.error("Error toggling document status:", error);
      const errorMessage = error.response?.data?.message || "Error updating document status.";
      dispatch(messageShow(errorMessage));
    }
  }, [dispatch, selectedGroupFilter, fetchDocuments]);

  const handleRemoveDoc = (doc) => {
    setDocToDelete(doc);
    setIsHardDelete(false);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;

    try {
      const { data } = await dispatch(removeDocument(docToDelete._id, isHardDelete ? 'hard' : 'soft'));
      if (data.success) {
        dispatch(messageShow(data.message || "Document deleted successfully."));
        fetchDocuments(selectedGroupFilter);
      } else {
        dispatch(messageShow(data.message || "Failed to delete document."));
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      const errorMessage = error.response?.data?.message || "Error deleting document.";
      dispatch(messageShow(errorMessage));
    } finally {
      setIsDeleteDialogOpen(false);
      setDocToDelete(null);
      setIsHardDelete(false);
    }
  };

  const handleGroupFilterChange = (groupFilter) => {
    setSelectedGroupFilter(groupFilter);
    setPage(1);
    fetchDocuments(groupFilter);
  };

  const getGroupLabel = (groupValue) => {
    const group = groups.find(g => g.value === groupValue);
    return group ? group.label : groupValue || "Uncategorized";
  };

  const handleView = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const renderCard = (doc) => {
    const isUploaded = !!doc.fileUrl;
    const formattedDate = doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) : 'N/A';

    const isPublished = doc.isPublished !== false;

    return (
      <Grid item xs={12} sm={6} md={4} key={doc._id}>
        <Paper 
          className={classes.card} 
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {!isPublished && <div className={classes.unpublishedOverlay} />}
          
          <div className={classes.cardInner}>
            <div className={classes.cardHeader}>
              <div className={classes.iconWrapper}>
                <DescriptionIcon />
              </div>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" className={classes.title} noWrap>
                  {doc.title}
                </Typography>
              </Box>
            </div>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                {doc.group && (
                  <Chip
                    label={getGroupLabel(doc.group)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                {permissions.canEdit && (
                  <>
                    <Tooltip title={isPublished ? "Click to Unpublish" : "Click to Publish"}>
                      <Switch
                        checked={isPublished}
                        onChange={() => onPublishToggle(doc)}
                        size="small"
                        color="primary"
                      />
                    </Tooltip>
                  <Tooltip title="Edit Document">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditStart(doc)}
                      sx={{ backgroundColor: '#f0f9ff', ml: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  </>
                )}
                {permissions.canDelete && (
                  <Tooltip title="Delete Document">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveDoc(doc)}
                      sx={{ ml: 0.5, backgroundColor: '#fff5f5' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

          <div
            className={`${classes.statusBadge} ${
              isUploaded ? classes.statusUploaded : classes.statusEmpty
            }`}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {isUploaded ? (
                <CheckIcon sx={{ fontSize: 13 }} />
              ) : (
                <PendingIcon sx={{ fontSize: 13 }} />
              )}
              {isUploaded ? "Uploaded" : "Missing"}
            </Box>
          </div>

          <div className={classes.metadata}>
            <Typography variant="caption">
              Added on: <strong>{formattedDate}</strong>
            </Typography>
          </div>

          <Typography className={classes.filename} noWrap>
            {isUploaded ? doc.fileUrl.split("/").pop() : "No file selected"}
          </Typography>

          {isUploaded && (
            <div className={classes.actionBar}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ViewIcon />}
                onClick={() => handleView(doc.fileUrl)}
                className={classes.actionButton}
                size="small"
              >
                View Document
              </Button>
            </div>
          )}
          </div>
        </Paper>
      </Grid>
    );
  };

  const renderHelper = () => (
    <Box sx={{ p: 0, pt: 4 }}>
      <Box
        sx={{
          backgroundColor: '#F8F9FE',
          borderRadius: '8px',
          border: '1px solid #E9ECEF',
          p: 3
        }}
      >
        <Box display="flex" alignItems="center" mb={2} sx={{ color: '#5E72E4' }}>
          <LightbulbOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            DOCUMENTS QUICK TIPS
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="flex-start">
              <ChevronRightIcon sx={{ color: '#5E72E4', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#525F7F', lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600, color: '#32325D' }}>Organize by Groups:</span> Use document groups to categorize your files (e.g., "General", "Policy"). This helps keep your documents organized and easily accessible.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="flex-start">
              <ChevronRightIcon sx={{ color: '#5E72E4', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#525F7F', lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600, color: '#32325D' }}>PDF Support:</span> Currently, only PDF files are supported for document management to ensure cross-platform compatibility.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="flex-start">
              <ChevronRightIcon sx={{ color: '#5E72E4', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#525F7F', lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600, color: '#32325D' }}>Secure Storage:</span> All uploaded documents are stored securely. You can view or remove them at any time from this dashboard.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  if (isPermissionChecking) {
    return <BrandedLoading />;
  }

  if (!permissions.canView) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
         <SecurityIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
         <Typography variant="h5" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>Access Restricted</Typography>
         <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 400 }}>
           You do not have permission to view this module. Please contact your administrator if you believe this is an error.
         </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Toolbar className={classes.toolbar}>
        <Typography variant="h6" color="inherit" className={classes.grow} sx={{ fontWeight: 700 }}>
          Document Manager
        </Typography>
      </Toolbar>

      <div className={classes.rootContainer}>
        {isLoading ? (
          <BrandedLoading />
        ) : (
          <Box sx={{ maxWidth: 1200, margin: "0 auto" }}>
            <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
              Manage all your legal and policy documents in one place. These are stored securely and tracked with timestamps.
            </Typography>

            {/* Group Filter Chips */}
            <Box sx={{ mb: 3, display: 'flex', overflowX: 'auto', gap: 1, pb: 1, '::-webkit-scrollbar': { height: '6px' }, '::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' } }}>
              <Chip
                label="All Groups"
                onClick={() => handleGroupFilterChange("all")}
                color={selectedGroupFilter === "all" ? "primary" : "default"}
                variant={selectedGroupFilter === "all" ? "filled" : "outlined"}
                clickable
                sx={{ fontWeight: 600 }}
              />
              {groups.map(group => (
                <Chip
                  key={group.value}
                  label={group.label}
                  onClick={() => handleGroupFilterChange(group.value)}
                  color={selectedGroupFilter === group.value ? "primary" : "default"}
                  variant={selectedGroupFilter === group.value ? "filled" : "outlined"}
                  clickable
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Box>

            {documents.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: '1px dashed #cbd5e1'
                }}
              >
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e2e8f0', borderRadius: '50%' }}>
                  <PostAddIcon sx={{ fontSize: 48, color: '#64748b' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#334155', mb: 1, fontWeight: 600 }}>
                  No documents found
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                  Upload a new document to get started with this collection.
                </Typography>
                {permissions.canCreate && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UploadIcon />}
                    onClick={() => {
                       setEditingDoc(null);
                       setNewDocTitle("");
                       setNewDocGroup("");
                       setIsAddDialogOpen(true);
                    }}
                  >
                    Upload New Document
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <Grid container spacing={3}>
                  {documents.map(doc => renderCard(doc))}

                  {permissions.canCreate && page === 1 && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper
                        className={classes.card}
                        elevation={0}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed #cbd5e1',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          minHeight: '260px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            backgroundColor: '#eff6ff',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.05)'
                          },
                        }}
                        onClick={() => {
                          setEditingDoc(null);
                          setNewDocTitle("");
                          setNewDocGroup("");
                          setIsAddDialogOpen(true);
                        }}
                      >
                        <Box sx={{
                          backgroundColor: '#f1f5f9',
                          borderRadius: '50%',
                          p: 2,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <AddIcon sx={{ fontSize: 40, color: '#64748b' }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 700 }}>
                          Upload New Document
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5 }}>PDF files only, up to 15MB</Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {count > rowsPerPage && (
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={Math.ceil(count / rowsPerPage)}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
            {renderHelper()}
          </Box>
        )}
      </div>

      <Dialog
        open={isAddDialogOpen}
        onClose={() => { setIsAddDialogOpen(false); setSelectedFile(null); setEditingDoc(null); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            minWidth: 500,
            minHeight: 450
          }
        }}
      >
        <DialogTitle>{editingDoc ? "Edit Document" : "Upload New Document"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            {editingDoc ? "Update document details or replace" : "Enter document details and select"} a PDF file to upload.
          </Typography>
          <MuiTextField
            autoFocus
            fullWidth
            label="Document Title"
            variant="outlined"
            value={newDocTitle}
            onChange={(e) => setNewDocTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            required
          />

          {/* Group Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              freeSolo
              options={groups.map((option) => option.value)}
              value={newDocGroup}
              onChange={(event, newValue) => {
                setNewDocGroup(newValue);
              }}
              onInputChange={(event, newInputValue) => {
                setNewDocGroup(newInputValue);
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="Document Group"
                  placeholder="Add new group or choose"
                  variant="outlined"
                  required
                />
              )}
            />
          </FormControl>

          <Box sx={{ width: '100%', mb: 2 }}>
            <FormControl component="fieldset" fullWidth required>
              <FormLabel component="legend">Storage Type</FormLabel>
              <RadioGroup
                row
                aria-label="storageType"
                name="storageType"
                value={storageType}
                onChange={(e) => setStorageType(e.target.value)}
              >
                <FormControlLabel value="public" control={<Radio size="small" />} label={<Typography variant="body2">Public</Typography>} />
                <FormControlLabel value="secured" control={<Radio size="small" />} label={<Typography variant="body2">Secured</Typography>} />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ width: '100%', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Published</Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={{ width: '100%' }}>
            <input
              type="file"
              accept=".pdf"
              id="new-doc-file-input"
              style={{ display: "none" }}
              onChange={onUpload}
            />

            <label htmlFor="new-doc-file-input" style={{ width: '100%', display: 'block' }}>
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<UploadIcon />}
                disabled={Object.values(isUploading).some(v => v)}
              >
                {selectedFile ? "Change PDF File" : (editingDoc ? "Replace PDF File" : "Select PDF File")}
              </Button>
            </label>
          </Box>

          {/* Selected File Name display */}
          {selectedFile && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ color: '#64748b', fontSize: 20 }} />
              <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedFile.name}
              </Typography>
            </Box>
          )}

          {editingDoc && !selectedFile && (
             <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
               <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
               <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600 }}>
                 Current file: {editingDoc.fileUrl.split("/").pop()}
               </Typography>
             </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setIsAddDialogOpen(false); setSelectedFile(null); setEditingDoc(null); }} disabled={Object.values(isUploading).some(v => v)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!newDocTitle.trim() || !newDocGroup || !newDocGroup.trim() || (!editingDoc && (!storageType || !selectedFile)) || (editingDoc && selectedFile && !storageType) || Object.values(isUploading).some(v => v)}
            startIcon={Object.values(isUploading).some(v => v) ? <BrandedLoading size={20} /> : <SaveIcon />}
          >
            {Object.values(isUploading).some(v => v) ? (editingDoc ? "Updating..." : "Saving...") : (editingDoc ? "Update Document" : "Save Document")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
           Delete Document
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete <strong>{docToDelete?.title}</strong>?
          </Typography>
          
          <Box sx={{ mt: 2, mb: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
             <FormControlLabel
                control={
                  <Checkbox
                    checked={isHardDelete}
                    onChange={(e) => setIsHardDelete(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: isHardDelete ? '#d32f2f' : 'inherit' }}>
                    Permanently Delete (Hard Delete)
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
              <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4, mt: 0.5 }}>
                 {isHardDelete 
                    ? "Warning: This action cannot be undone. The file will be permanently removed from storage."
                    : "Standard delete moves the document to trash."}
              </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained" 
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

Documents.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Documents);
